import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPatch, TENANT_TOKEN_KEY } from '../../api/client';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { tokens } from '../../design-system/tokens';
import { DEFAULT_ORG_TIME_ZONE, ORG_TIME_ZONES } from '../../lib/orgTimeZones';

type BrandLogoData = {
  logoUrl: string | null;
  mimeType: string | null;
  updatedAt: string | null;
  timeZone: string | null;
};

export function BrandSettingsPage() {
  const { can, reload: reloadSession } = useTenantPermissions();
  const canWrite = can('web.integrations.write');
  const [data, setData] = useState<BrandLogoData | null>(null);
  const [timeZone, setTimeZone] = useState(DEFAULT_ORG_TIME_ZONE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    setErr('');
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const d = await apiGet<BrandLogoData>('/api/console/brand-logo', token);
      setData(d);
      setTimeZone(d.timeZone || DEFAULT_ORG_TIME_ZONE);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (file: File | null) => {
    if (!file || !canWrite) return;
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    setErr('');
    setOkMsg('');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch('/api/console/brand-logo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const j = (await r.json()) as {
        success?: boolean;
        data?: BrandLogoData;
        error?: { message?: string };
      };
      if (!r.ok || !j.success || !j.data) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      setData(j.data);
      setOkMsg('Logo actualizado.');
      await reloadSession();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveTimeZone = async () => {
    if (!canWrite) return;
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    setErr('');
    setOkMsg('');
    setBusy(true);
    try {
      const saved = await apiPatch<{ timeZone: string }>('/api/console/brand', { timeZone }, token, {
        showErrorGrowl: false,
      });
      setTimeZone(saved.timeZone);
      setData((current) => (current ? { ...current, timeZone: saved.timeZone } : current));
      setOkMsg('Uso horario guardado. El agente saluda con esa hora.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!canWrite) return;
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    setErr('');
    setOkMsg('');
    setBusy(true);
    try {
      const r = await fetch('/api/console/brand-logo', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json()) as { success?: boolean; error?: { message?: string } };
      if (!r.ok || !j.success) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      setData({ logoUrl: null, mimeType: null, updatedAt: null, timeZone });
      setOkMsg('Logo eliminado.');
      await reloadSession();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p style={{ color: tokens.semantic.textSecondary }}>Cargando…</p>;
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 22, color: tokens.semantic.textPrimary }}>Marca</h1>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: tokens.semantic.textSecondary, lineHeight: 1.5 }}>
        Logo de tu empresa en la consola y en la app móvil. PNG, JPEG, WebP o SVG · máx. 1 MB.
      </p>

      {err ? <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p> : null}
      {okMsg ? <p style={{ color: '#047857', fontSize: 14 }}>{okMsg}</p> : null}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 12,
            border: `1px solid ${tokens.semantic.border}`,
            background: tokens.colors.neutral[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <img
            src={data?.logoUrl || '/logo.png'}
            alt=""
            width={72}
            height={72}
            style={{ objectFit: 'contain' }}
          />
        </div>
        {canWrite ? (
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Subir logo
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                disabled={busy}
                style={{ display: 'block', marginTop: 8 }}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  void upload(f);
                }}
              />
            </label>
            <button
              type="button"
              disabled={busy || !data?.logoUrl}
              onClick={() => void remove()}
              style={{
                marginTop: 12,
                padding: '8px 14px',
                borderRadius: 8,
                border: `1px solid ${tokens.semantic.border}`,
                background: '#fff',
                cursor: busy || !data?.logoUrl ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Quitar logo
            </button>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: tokens.semantic.textSecondary }}>
            Solo usuarios con permiso de canales pueden cambiar el logo.
          </p>
        )}
      </div>

      <div style={{ marginTop: 32, maxWidth: 360 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, color: tokens.semantic.textPrimary }}>Uso horario</h2>
        <p style={{ margin: '0 0 12px', fontSize: 14, color: tokens.semantic.textSecondary, lineHeight: 1.5 }}>
          El agente recibe la hora de esta zona en cada mensaje, para saludar según el momento del día del cliente.
        </p>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Zona
          <select
            value={timeZone}
            disabled={!canWrite || busy}
            onChange={(event) => setTimeZone(event.target.value)}
            style={{
              display: 'block',
              marginTop: 8,
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${tokens.semantic.border}`,
              background: '#fff',
              fontSize: 14,
            }}
          >
            {ORG_TIME_ZONES.some((zone) => zone.id === timeZone) ? null : (
              <option value={timeZone}>{timeZone}</option>
            )}
            {ORG_TIME_ZONES.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.label}
              </option>
            ))}
          </select>
        </label>
        {canWrite ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void saveTimeZone()}
            style={{
              marginTop: 8,
              padding: '8px 14px',
              borderRadius: 8,
              border: 'none',
              background: tokens.colors.primary[600],
              color: '#fff',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Guardar uso horario
          </button>
        ) : null}
      </div>
    </div>
  );
}
